// src/components/ApplicationDropdown.jsx
// Exact same pattern as CompanyDropdown
// Select karte hi onSelect(app) callback milta hai jisme { id, appName, appPackage, ... } hota hai

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Package, ChevronDown, X, Loader } from 'lucide-react';

// ─── adjust this import to your actual API service ───────────────────────────
import apiService from '../services/apiService';

const ApplicationDropdown = ({
  value,           // currently selected appPackage (string)
  onChange,        // (appObject | null) => void
  placeholder = 'Search & select application...',
  disabled = false,
}) => {
  const [query,     setQuery]     = useState('');
  const [options,   setOptions]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [open,      setOpen]      = useState(false);
  const [selected,  setSelected]  = useState(null);  // full app object
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // If parent passes a value (appPackage), fetch that app to show its label
  useEffect(() => {
    if (value && !selected) {
      (async () => {
        try {
          const res = await apiService.get(`custom-apps?search=${encodeURIComponent(value)}&limit=5`);
          const list = res?.data?.data || res?.data || [];
          const found = list.find(a => a.packageName === value || a._id === value || a.id === value);
          if (found) setSelected(normalise(found));
        } catch {}
      })();
    }
    if (!value) { setSelected(null); setQuery(''); }
  }, [value]);

  const normalise = raw => ({
    id:         raw._id  || raw.id,
    appName:    raw.appName    || raw.name || '',
    appPackage: raw.packageName || raw.appPackage || '',
    version:    raw.version    || '',
    status:     raw.status     || '',
    template:   raw.template   || '',
    logoUrl:    raw.logoUrl    || null,
  });

  const fetchApps = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const endpoint = q
        ? `custom-apps?search=${encodeURIComponent(q)}&limit=20&isActive=true`
        : `custom-apps?limit=20&isActive=true`;
      const res  = await apiService.get(endpoint);
      const list = res?.data?.data || res?.data || [];
      setOptions(list.map(normalise));
    } catch {
      setOptions([]);
    }
    setLoading(false);
  }, []);

  const handleSearch = e => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchApps(q), 300);
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    fetchApps(query);
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  const handleSelect = app => {
    setSelected(app);
    setQuery('');
    setOpen(false);
    onChange(app);
  };

  const handleClear = e => {
    e.stopPropagation();
    setSelected(null);
    setQuery('');
    onChange(null);
  };

  // Status colour
  const statusColor = s => ({ Published:'#10b981', Draft:'#f59e0b', Building:'#3b82f6', Failed:'#ef4444' }[s] || '#94a3b8');

  return (
    <div ref={wrapRef} style={{ position:'relative', width:'100%' }}>

      {/* Trigger */}
      <div
        onClick={handleOpen}
        style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'10px 13px', border:`1.5px solid ${open?'#3b82f6':'#e2e8f0'}`,
          borderRadius:9, background: disabled ? '#f8fafc' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition:'border-color .15s', boxSizing:'border-box', minHeight:44,
          boxShadow: open ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
        }}>
        {selected ? (
          <>
            {selected.logoUrl
              ? <img src={selected.logoUrl} alt="" style={{width:24,height:24,borderRadius:5,objectFit:'cover',flexShrink:0}}/>
              : <div style={{width:24,height:24,borderRadius:5,background:'#eff6ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <Package size={13} color="#3b82f6"/>
                </div>
            }
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selected.appName}</div>
              <div style={{fontSize:11,color:'#94a3b8',fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selected.appPackage}</div>
            </div>
            {!disabled && (
              <button onClick={handleClear} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:2,display:'flex',flexShrink:0}}>
                <X size={14}/>
              </button>
            )}
          </>
        ) : (
          <>
            <Package size={15} color="#94a3b8" style={{flexShrink:0}}/>
            <span style={{fontSize:13,color:'#94a3b8',flex:1}}>{placeholder}</span>
            <ChevronDown size={15} color="#94a3b8" style={{flexShrink:0,transform:open?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}/>
          </>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, zIndex:999,
          background:'#fff', borderRadius:10, border:'1.5px solid #e2e8f0',
          boxShadow:'0 12px 40px rgba(0,0,0,0.14)', overflow:'hidden',
        }}>
          {/* Search input */}
          <div style={{padding:'10px 12px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',gap:8}}>
            <Search size={14} color="#94a3b8" style={{flexShrink:0}}/>
            <input
              ref={inputRef}
              value={query}
              onChange={handleSearch}
              placeholder="Search by name or package..."
              style={{flex:1,border:'none',outline:'none',fontSize:13,color:'#1e293b',background:'transparent'}}
            />
            {loading && <Loader size={13} color="#94a3b8" style={{animation:'spin 1s linear infinite',flexShrink:0}}/>}
          </div>

          {/* Options list */}
          <div style={{maxHeight:260,overflowY:'auto'}}>
            {options.length === 0 && !loading && (
              <div style={{padding:'20px 16px',textAlign:'center',color:'#94a3b8',fontSize:13}}>
                <Package size={24} style={{marginBottom:8,opacity:0.3,display:'block',margin:'0 auto 8px'}}/>
                No applications found
              </div>
            )}
            {options.map(app => {
              const isSelected = selected?.id === app.id;
              return (
                <div key={app.id}
                  onClick={() => handleSelect(app)}
                  style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'10px 14px', cursor:'pointer',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    borderBottom:'1px solid #f8fafc',
                    transition:'background .1s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background='#f8fafc'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background='transparent'; }}>
                  {/* App icon / logo */}
                  {app.logoUrl
                    ? <img src={app.logoUrl} alt="" style={{width:34,height:34,borderRadius:7,objectFit:'cover',flexShrink:0}}/>
                    : <div style={{width:34,height:34,borderRadius:7,background:isSelected?'#dbeafe':'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <Package size={16} color={isSelected?'#3b82f6':'#94a3b8'}/>
                      </div>
                  }
                  {/* Info */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:13,fontWeight:600,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{app.appName}</span>
                      {app.version && <span style={{fontSize:10,color:'#94a3b8',flexShrink:0}}>v{app.version}</span>}
                    </div>
                    <div style={{fontSize:11,color:'#94a3b8',fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:2}}>{app.appPackage}</div>
                  </div>
                  {/* Status dot */}
                  {app.status && (
                    <div style={{display:'flex',alignItems:'center',gap:4,flexShrink:0}}>
                      <div style={{width:7,height:7,borderRadius:'50%',background:statusColor(app.status)}}/>
                      <span style={{fontSize:10,color:statusColor(app.status),fontWeight:600}}>{app.status}</span>
                    </div>
                  )}
                  {isSelected && <div style={{width:16,height:16,borderRadius:'50%',background:'#3b82f6',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><span style={{color:'#fff',fontSize:9,fontWeight:700}}>✓</span></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ApplicationDropdown;